import { Fragment } from 'react';
import { Link, useParams } from 'react-router';
import { ArrowRight, ChevronDown } from 'lucide-react';

import { OpenAppButton } from '@/components/ui/open-app-dialog';
import { BookDemoButton } from '@/components/ui/book-demo-dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Container } from '@/components/ui/container';
import { Reveal } from '@/components/ui/reveal';
import { Eyebrow, Section, SectionHeading } from '@/components/ui/section';
import { getIcon } from '@/components/ui/icon-map';
import { FinalCta } from '@/components/sections/FinalCta';
import { TreasuryWorkflow } from '@/components/sections/TreasuryWorkflow';
import { SecurityWorkflow } from '@/components/sections/SecurityWorkflow';
import {
  PolicyCapabilities,
  PolicyCapabilitiesFaq,
} from '@/components/sections/PolicyCapabilities';
import { PolicyPlayground } from '@/components/sections/PolicyPlayground';
import { pageContent } from '@/content/pages';
import { getCanonicalPath } from '@/routes';
import { NotFound } from './NotFound';

/** Template for Product feature pages and Solutions pages (driven by slug). */
export function MarketingPage() {
  const { slug } = useParams();
  const content = slug ? pageContent[slug] : undefined;
  const isPolicyPage = slug === 'policy-engine';

  if (!content) {
    return <NotFound />;
  }

  return (
    <>
      <section className="relative overflow-hidden bg-white pt-28 sm:pt-32 lg:pt-36">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute inset-x-0 top-0 h-[440px] bg-grid-faint bg-[size:60px_60px] [mask-image:radial-gradient(60%_55%_at_50%_0%,black,transparent)]" />
        </div>
        <Container className="relative">
          <div className="mx-auto flex  flex-col items-flex-start text-left">
            <Eyebrow>{content.eyebrow}</Eyebrow>
            <h1 className="mt-5 text-balance text-[2.4rem] font-medium leading-[1.05] tracking-tight text-ink-900 sm:text-5xl lg:text-6xl">
              {content.title}
              {content.titleAccent && (
                <>
                  {' '}
                  <span className="text-ink-300">{content.titleAccent}</span>
                </>
              )}
            </h1>
            <p className="mt-6 max-w-2xl text-pretty text-base leading-relaxed text-ink-500 sm:text-lg">
              {content.subtitle}
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
              <BookDemoButton
                variant="primary"
                size="lg"
                analyticsLocation="page_hero"
              >
                Book a demo
                <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </BookDemoButton>
              {isPolicyPage ? (
                <Button href="#policy-playground" variant="secondary" size="lg">
                  Try a policy{' '}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Button>
              ) : (
                <OpenAppButton
                  variant="secondary"
                  size="lg"
                  analyticsLocation="page_hero"
                >
                  Open the app
                </OpenAppButton>
              )}
            </div>
          </div>
        </Container>
      </section>

      {isPolicyPage ? (
        <PolicyCapabilities />
      ) : (
        <Section className="bg-white py-10 sm:py-12 lg:py-14">
          <Container>
            <div className="grid gap-4 sm:grid-cols-2">
              {content.features.map((f, i) => {
                const Icon = getIcon(f.icon);
                return (
                  <Reveal key={f.title} delay={i * 80}>
                    <Card interactive className="flex h-full flex-col p-7">
                      <div className="grid size-12 place-items-center rounded-xl border-none border-ember-100 bg-ember-50 text-ember-600">
                        <Icon className="size-6" />
                      </div>
                      <h2 className="mt-5 text-xl font-semibold text-ink-900">
                        {f.title}
                      </h2>
                      <p className="mt-2 text-sm leading-relaxed text-ink-500">
                        {f.body}
                      </p>
                    </Card>
                  </Reveal>
                );
              })}
            </div>
          </Container>
        </Section>
      )}

      {content.workflow && (
        <Section className="border-t border-hairline bg-surface">
          <Container>
            <SectionHeading
              title={content.workflow.title}
              description={content.workflow.description}
              align="left"
              className="max-w-3xl [&_h2]:leading-[1.2]"
            />
            {content.workflow.kind === 'security' ? (
              <SecurityWorkflow workflow={content.workflow} />
            ) : (
              <TreasuryWorkflow workflow={content.workflow} />
            )}
            {content.workflow.steps.every((step) => step.description) && (
              <details className="mt-7 rounded-xl border border-hairline bg-white px-5 py-4 sm:px-6">
                <summary className="cursor-pointer text-sm font-medium text-ink-900">
                  Read the workflow, step by step
                </summary>
                <ol className="mt-5 grid list-decimal gap-5 pl-5 text-sm leading-relaxed text-ink-600 sm:grid-cols-2 sm:gap-x-10">
                  {content.workflow.steps.map((step) => (
                    <li key={step.title} className="pl-1">
                      <h3 className="font-medium text-ink-900">{step.title}</h3>
                      <p className="mt-2">{step.description}</p>
                    </li>
                  ))}
                </ol>
              </details>
            )}
          </Container>
        </Section>
      )}

      {content.faq && (
        <Section className="border-t border-hairline bg-white">
          <Container>
            <SectionHeading title={content.faq.title} align="left" />
            <div className="mt-10">
              {content.faq.items.map(({ q, a }, index) => (
                <Fragment key={q}>
                  <details
                    name="page-faq"
                    className="group border-t border-hairline"
                  >
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <h3 className="text-base font-medium text-ink-900 sm:text-lg">
                        {q}
                      </h3>
                      <ChevronDown
                        aria-hidden="true"
                        className="size-5 shrink-0 text-ink-400 transition-transform duration-300 group-open:rotate-180"
                      />
                    </summary>
                    <p className="max-w-3xl pb-5 pr-9 text-sm leading-relaxed text-ink-500">
                      {a}
                    </p>
                  </details>
                  {isPolicyPage && index === 0 && <PolicyCapabilitiesFaq />}
                </Fragment>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {isPolicyPage && <PolicyPlayground />}

      <Section className="border-t border-hairline bg-surface">
        <Container className="grid gap-10 md:grid-cols-2">
          <div>
            <h2 className="text-2xl font-medium tracking-tight text-ink-900">
              Explore related capabilities
            </h2>
            <ul className="mt-5 space-y-4">
              {content.relatedSlugs.map((relatedSlug) => (
                <li key={relatedSlug}>
                  <Link
                    to={getCanonicalPath(relatedSlug)}
                    className="inline-flex items-center gap-2 text-ink-700 underline decoration-ink-200 underline-offset-4 hover:text-ink-900"
                  >
                    {pageContent[relatedSlug]?.eyebrow}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="text-2xl font-medium tracking-tight text-ink-900">
              Technical documentation
            </h2>
            <ul className="mt-5 space-y-4">
              {content.resources.map((resource) => (
                <li key={resource.href}>
                  <a
                    href={resource.href}
                    className="text-ink-700 underline decoration-ink-200 underline-offset-4 hover:text-ink-900"
                  >
                    {resource.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </Container>
      </Section>

      <FinalCta />
    </>
  );
}
